import React, { useEffect, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { BACKGROUNDS } from 'themes'
import { detectIpad, detectMobile } from 'common/adapters/DeviceDetectAdapter'
import GamesService from 'common/Services/GamesService'
import getTexts from 'utils/localization'
import { HEADER_MOBILE_HEIGHT, TOPBAR_MOBILE_HEIGHT } from 'configs/ui'
import DropdownMenu from 'ui/Dropdown'
import Text from 'ui/Text'
import Button from 'ui/controls/Button'

// Constants
const t = getTexts()
const STORAGE_KEY_SORT_METHOD = 'sortMethod'
const STORAGE_KEY_SORT_REVERSED = 'sortMethodReversed'
const DEFAULT_SORT_METHOD = 'new'
const DEFAULT_POPULAR_CATEGORIES = ['alle spil', 'populære']
const JACKPOT_CATEGORY = 'jackpot'

const DropdownItem = styled.div`
    padding: 7px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    transition: all 0.3s ease;
    border-radius: 5px;
    .hasHover &:hover {
        background-color: ${BACKGROUNDS.grey300};
        filter: brightness(120%);
    }
`

const sortingMethods = [
    {
        label: t.gridControls.new,
        name: 'new',
        order: ['Først ', 'Sidst'],
    },
    { label: t.gridControls.popular, name: 'popular', order: ['Høj', 'Lav'] },
    { label: t.gridControls.alphabet, name: 'name', order: ['A', 'Å'] },
    { label: t.gridControls.minStake, name: 'minStake', order: ['Høj', 'Lav'] },
    { label: t.gridControls.TBP, name: 'tbp', order: ['Høj', 'Lav'] },
]

const SortButton = ({
    category,
    gamesToMapAll,
    setGamesToMapAll,
    setGamesToMap,
    hideOnScroll,
}) => {
    // Helper functions
    const isStringTrue = (str) => str === 'true'
    const isMobileDevice = useCallback(
        () => detectMobile() && !detectIpad(),
        []
    )

    const getDefaultSortMethod = useCallback(
        () => {
            let method =
                localStorage.getItem(STORAGE_KEY_SORT_METHOD) ||
                DEFAULT_SORT_METHOD

            if (category) {
                const categoryLower = category.toLowerCase()

                if (DEFAULT_POPULAR_CATEGORIES.includes(categoryLower)) {
                    method =
                        localStorage.getItem(STORAGE_KEY_SORT_METHOD) ||
                        'popular'
                } else if (categoryLower === JACKPOT_CATEGORY) {
                    method =
                        localStorage.getItem(STORAGE_KEY_SORT_METHOD) ||
                        'jackpot'
                }
            }

            return method
        },
        [category]
    )

    // State
    const [sortingReversed, setSortingReversed] = useState(
        isStringTrue(localStorage.getItem(STORAGE_KEY_SORT_REVERSED) || 'false')
    )
    const [sortMethod, setSortMethod] = useState(getDefaultSortMethod())
    const [dropdownOpened, setDropdownOpened] = useState(false)
    const [clickOutside, setClickOutside] = useState(false)

    // Event handlers
    const toggleDropdown = () => {
        setDropdownOpened(clickOutside && !dropdownOpened)
    }

    const changeMethod = (method) => {
        // If same method selected, toggle the sort direction
        const newReversed = method === sortMethod && !sortingReversed

        setSortingReversed(newReversed)
        setSortMethod(method)

        localStorage.setItem(STORAGE_KEY_SORT_REVERSED, newReversed.toString())
        localStorage.setItem(STORAGE_KEY_SORT_METHOD, method)
    }

    const sortGames = useCallback(
        () => {
            const method =
                localStorage.getItem(STORAGE_KEY_SORT_METHOD) || sortMethod
            const methodReversed =
                localStorage.getItem(STORAGE_KEY_SORT_REVERSED) ||
                sortingReversed.toString()

            GamesService.sortGames(
                gamesToMapAll,
                setGamesToMapAll,
                setGamesToMap,
                method,
                methodReversed
            )
        },
        [
            gamesToMapAll,
            setGamesToMapAll,
            setGamesToMap,
            sortMethod,
            sortingReversed,
        ]
    )

    // Render helpers
    const renderSortOrderDisplay = (method, isActive) => {
        const isReversed = isActive && sortingReversed

        return (
            <Text
                style={{ display: 'flex', flex: 1, justifyContent: 'flex-end' }}
            >
                <span style={{ order: isReversed ? '3' : '1' }}>
                    {method.order[0]}
                </span>
                <span style={{ order: '2' }}>&nbsp;-&nbsp;</span>
                <span style={{ order: isReversed ? '1' : '3' }}>
                    {method.order[1]}
                </span>
            </Text>
        )
    }

    // Effects
    useEffect(
        () => {
            setSortMethod(getDefaultSortMethod())
            sortGames()
        },
        [
            gamesToMapAll,
            sortMethod,
            sortingReversed,
            getDefaultSortMethod,
            sortGames,
        ]
    )

    // Close dropdown on scroll for mobile
    useEffect(
        () => {
            if (dropdownOpened && hideOnScroll && isMobileDevice()) {
                setDropdownOpened(false)
            }
        },
        [hideOnScroll]
    )

    // Close dropdown when clicked outside
    useEffect(
        () => {
            if (!clickOutside && dropdownOpened) {
                setDropdownOpened(false)
            }
        },
        [clickOutside, dropdownOpened]
    )

    return (
        <Button
            onClick={toggleDropdown}
            textsize="16px"
            padding="5px 25px"
            black
        >
            {isMobileDevice() ? 'Sorter efter' : 'Sorter'}
            <DropdownMenu
                sorting
                top={
                    isMobileDevice()
                        ? `calc(${TOPBAR_MOBILE_HEIGHT} + ${HEADER_MOBILE_HEIGHT} + 65px)`
                        : '45px'
                }
                bottom="auto"
                open={dropdownOpened}
                setDropdownOpened={() => setClickOutside(!dropdownOpened)}
            >
                {sortingMethods.map((method) => (
                    <DropdownItem
                        key={method.name}
                        onClick={() => changeMethod(method.name)}
                        active={sortMethod === method.name}
                    >
                        <Text>{method.label}</Text>
                        {renderSortOrderDisplay(
                            method,
                            sortMethod === method.name
                        )}
                    </DropdownItem>
                ))}
            </DropdownMenu>
        </Button>
    )
}

SortButton.propTypes = {
    category: PropTypes.string.isRequired,
    gamesToMapAll: PropTypes.array.isRequired,
    setGamesToMapAll: PropTypes.func.isRequired,
    setGamesToMap: PropTypes.func.isRequired,
    hideOnScroll: PropTypes.bool.isRequired,
}

export default SortButton
